export type ApiResponseErrorProperties = {
  type?: string;
  code?: number;
  message: string;
  status?: string;
  /**
   * DRF field-level validation errors из тела ответа —
   * `{ "field_name": ["Сообщение 1", "Сообщение 2"] }`.
   * Читается UI-слоем, когда ошибку надо показать рядом с полем формы, а не
   * единым toast'ом.
   */
  fields?: Record<string, string[]>;
  /**
   * Сырое тело ответа. Нужно там, где приложение разбирает структуру само:
   * у analyst из него читаются `next_available_at` и реквизиты плана при 429
   * (BACK-732). Остальным приложениям поле безвредно.
   */
  data?: Record<string, unknown>;
  /**
   * Ответа не было ВООБСЕ: обрыв сети, таймаут, отвергнутый origin или
   * отменённый запрос. Признак явный, потому что по коду это не выводится —
   * клиент ставит себе 503, и ровно такой же код приходит настоящим ответом
   * сервера (AF-537).
   */
  noResponse?: boolean;
};

const API_RESPONSE_ERROR_NAME = 'ApiResponseError';

/**
 * Ошибка, в которую превращается любой неуспешный ответ бэкенда.
 *
 * `Object.setPrototypeOf` и `name` стояли только у deals; в общий клиент
 * перенесены оба (AF-13).
 *
 * **`setPrototypeOf` при сегодняшнем `target: esnext` не делает ничего** —
 * и удалять его всё равно нельзя. Замер (AF-13, 26.08), исполнением:
 *
 *     class Base extends Error { constructor(m) { super(m); } }
 *     new Base('x') instanceof Base   → true      // БЕЗ setPrototypeOf
 *
 * Строка нужна при `target: es5` и ниже: там наследование от встроенного
 * `Error` теряет цепочку прототипов, и `instanceof ApiResponseError` начинает
 * давать `false`. Понижение таргета — вещь, которая случается при подключении
 * старого потребителя, и случается не в этом файле.
 *
 * Следствие, важное для того, кто придёт с порчей: **снятие этой строки не
 * роняет ни одного теста**, и это не дыра в покрытии, а свойство стенда.
 * Проверить её можно только сборкой с понижённым таргетом; отдельную пробу
 * решили не строить — она защищала бы от будущего понижения, а не от
 * сегодняшнего дефекта, и первое же её падение списали бы на конфигурацию.
 *
 * `name` — другое дело: он нужен всегда (иначе Sentry группирует ошибки как
 * безымянные `Error`) и покрыт тестом, снятие роняет «называет себя
 * ApiResponseError».
 */
export class ApiResponseError extends Error {
  type?: string;
  code?: number;
  status?: string;
  fields?: Record<string, string[]>;
  data?: Record<string, unknown>;
  noResponse?: boolean;

  constructor(properties: ApiResponseErrorProperties) {
    super(properties.message);
    Object.setPrototypeOf(this, ApiResponseError.prototype);
    this.name = API_RESPONSE_ERROR_NAME;
    this.type = properties.type;
    this.code = properties.code;
    this.status = properties.status;
    this.fields = properties.fields;
    this.data = properties.data;
    this.noResponse = properties.noResponse;
  }
}

/**
 * Достаёт field-level ошибки для подсветки полей формы (через
 * `form.setErrorMap({ onServer: { fields } })`). Несколько сообщений одного
 * поля склеиваются пробелом.
 *
 * Возвращает `undefined`, если ошибка не `ApiResponseError` либо field-ошибок
 * в ней нет (DE-662). Имена полей бэкенда должны совпадать с именами полей
 * формы; несовпавшие просто не подсветятся — общий текст всё равно уходит в
 * toast потребителя.
 */
export const getApiFieldErrors = (
  error: unknown,
): Record<string, string> | undefined => {
  if (!(error instanceof ApiResponseError) || !error.fields) {
    return undefined;
  }
  const entries = Object.entries(error.fields)
    .filter(([, messages]) => messages.length > 0)
    .map(([field, messages]) => [field, messages.join(' ')] as const);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};
